// Where the platform's other apps live, worked out from where this page is being served.
//
// ── Why nothing here is a hostname ───────────────────────────────────────────────────────────────
// Every app used to carry the staging hostnames as constants (`https://clinic.easydent.easysoft.ro`
// and friends). That made deploying to production a CODE CHANGE: the public site would have shipped
// to the production domain still linking every card, every demo and every sign-in button back to
// staging. Nothing would have looked broken — the links resolve, they are just the wrong
// environment — which is the kind of fault that reaches customers.
//
// The apps are deployed as siblings under one environment root:
//
//   staging      easydent365.easysoft.ro   clinic.easydent365.easysoft.ro   patient.…
//   production   easydent365.com           clinic.easydent365.com           patient.…
//
// So the environment root is recoverable from ANY app's own hostname by dropping its own label, and
// every sibling is `<label>.<root>`. One rule, no configuration, and a new environment or a renamed
// domain needs no code change at all — which is the point.
//
// This is deliberately not a `VITE_` build arg: this monorepo has none, because a per-app arg would
// fork the shared Dockerfile's build cache across all eight images.

/** The apps that occupy a DNS label of their own. */
export type AppName = 'clinic' | 'patient' | 'operator' | 'supplier' | 'imaging' | 'docs';

/**
 * Labels stripped when recovering the environment root.
 *
 * `www` is here because the public site answers on both the apex and `www.` — without it, a visitor
 * who arrived at `www.` would be sent to `clinic.www.…`, a host that does not exist.
 */
const APP_LABELS: readonly string[] = ['clinic', 'patient', 'operator', 'supplier', 'imaging', 'docs', 'www'];

/**
 * Dev-server ports, taken from each app's `vite.config.ts`. Only used on localhost.
 *
 * ⚠️ These are the VITE dev ports (3000-3006), not the container ports the images publish
 * (5150-5156). They are easy to confuse and the two sets do not correspond.
 */
const DEV_PORTS: Record<AppName | 'public', number> = {
  clinic: 3000, patient: 3001, public: 3002, operator: 3003, supplier: 3004, imaging: 3005, docs: 3006,
};

/**
 * The documentation site.
 *
 * A genuine constant, and the one exception to everything above: the guides are static, public and
 * version-agnostic, so there is exactly ONE docs site serving every environment. A value that is
 * identical in staging and production is not environment configuration — deriving it per
 * environment is what created a second hostname to keep alive, which is why that was undone.
 */
export const DOCS_URL = 'https://docs.easydent365.com';

function isLocal(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || /^[\d.]+$/.test(hostname);
}

/**
 * The domain this environment's apps hang off, recovered from the current hostname.
 *
 * `clinic.easydent365.easysoft.ro` → `easydent365.easysoft.ro`; the public site's own
 * `easydent365.easysoft.ro` → itself. Exported because the public site needs its own origin for
 * canonical URLs and Open Graph tags.
 */
export function environmentRoot(hostname: string = window.location.hostname): string {
  const parts = hostname.split('.');
  return parts.length > 2 && APP_LABELS.includes(parts[0]) ? parts.slice(1).join('.') : hostname;
}

/** The public site's origin, no trailing slash. */
export function siteOrigin(): string {
  if (typeof window === 'undefined') return '';
  const { hostname, protocol } = window.location;
  if (isLocal(hostname)) return `${protocol}//${hostname}:${DEV_PORTS.public}`;
  return `https://${environmentRoot(hostname)}`;
}

/** A sibling app's origin, no trailing slash. */
export function appOrigin(app: AppName): string {
  if (typeof window === 'undefined') return '';
  const { hostname, protocol } = window.location;
  if (isLocal(hostname)) return `${protocol}//${hostname}:${DEV_PORTS[app]}`;
  // The docs site is shared across environments; everything else is a sibling of this one.
  if (app === 'docs') return DOCS_URL;
  return `https://${app}.${environmentRoot(hostname)}`;
}
